import { defineProperty as _defineProperty, objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import TooltipContent from './tooltip-content.esm.js';
import useControlled from '../../hooks/use-controlled.esm.js';
import useForkRef from '../../hooks/use-fork-ref.esm.js';
import useIsFocusVisible from '../../hooks/use-is-focus-visible.esm.js';
import { THEME_UI_DEFAULT_KEY } from '../../theme-provider/types.esm.js';
import { useSafeThemeUI } from '../../theme-provider/use-theme-ui.esm.js';
import deepmerge from '../../utils/deep-merge.esm.js';
import { get } from '../../utils/object.esm.js';
import { getSpace } from '../../utils/props-utils.esm.js';
import setRef from '../../utils/set-ref.esm.js';
import { useUniqueId } from '../../utils/unique-id.esm.js';
import Grow from '../_utils/grow/grow.esm.js';
import Popper from '../_utils/popper/popper.esm.js';

var _excluded = ["variant", "border", "arrow", "borderedArrow", "borderedArrowBackgroundColor", "borderedArrowBorderColor", "arrowProps", "children", "disableFocusListener", "disableHoverListener", "disableTouchListener", "enableWheelScrollListener", "enterDelay", "enterNextDelay", "enterTouchDelay", "id", "interactive", "leaveDelay", "leaveTouchDelay", "onClose", "onOpen", "open", "placement", "elevation", "PopperProps", "title", "TransitionComponent", "TransitionProps", "zIndex"];
var _arrowGenerator;
var TOOLTIP = 'csg-tui-tooltip-';

// default arrow when arrow is true
var arrowGenerator = (_arrowGenerator = {}, _defineProperty(_arrowGenerator, '&[x-placement*="bottom"] .csg-tui-tooltip-arrow', {
  top: 0,
  left: 0,
  marginTop: '-0.95em',
  marginLeft: 4,
  marginRight: 4,
  width: '2em',
  height: '1em',
  '&::before': {
    borderWidth: '0 1em 1em 1em',
    borderColor: 'transparent transparent currentcolor transparent'
  }
}), _defineProperty(_arrowGenerator, '&[x-placement*="top"] .csg-tui-tooltip-arrow', {
  bottom: 0,
  left: 0,
  marginBottom: '-0.95em',
  marginLeft: 4,
  marginRight: 4,
  width: '2em',
  height: '1em',
  '&::before': {
    borderWidth: '1em 1em 0 1em',
    borderColor: 'currentcolor transparent transparent transparent'
  }
}), _defineProperty(_arrowGenerator, '&[x-placement*="right"] .csg-tui-tooltip-arrow', {
  left: 0,
  marginLeft: '-0.95em',
  marginTop: 4,
  marginBottom: 4,
  height: '2em',
  width: '1em',
  '&::before': {
    borderWidth: '1em 1em 1em 0',
    borderColor: 'transparent currentcolor transparent transparent'
  }
}), _defineProperty(_arrowGenerator, '&[x-placement*="left"]  .csg-tui-tooltip-arrow', {
  right: 0,
  marginRight: '-0.95em',
  marginTop: 4,
  marginBottom: 4,
  height: '2em',
  width: '1em',
  '&::before': {
    borderWidth: '1em 0 1em 1em',
    borderColor: 'transparent transparent transparent currentcolor'
  }
}), _arrowGenerator);

// bordered arrow (borderedArrow === true)
var borderedArrowGenerator = function borderedArrowGenerator(backgroundColor, borderColor, boxShadow, border, scale) {
  var _ref;
  var size = "".concat(14 * (scale ? +scale : 1), "px");
  var commonParentStyles = {
    width: size,
    height: size,
    position: 'absolute',
    overflow: 'hidden',
    pointerEvents: 'none'
  };
  var commonChildStyles = {
    content: '""',
    position: 'absolute',
    width: size,
    height: size,
    bg: backgroundColor,
    boxShadow: boxShadow,
    border: border !== null && border !== void 0 ? border : borderColor ? "1px solid ".concat(borderColor) : '0px solid white',
    boxSizing: 'border-box'
  };
  return _ref = {}, _defineProperty(_ref, '&[x-placement*="bottom"] .csg-tui-tooltip-arrow', _objectSpread2(_objectSpread2({
    top: 0,
    transform: 'translateY(-100%)'
  }, commonParentStyles), {}, {
    '&::before': _objectSpread2({
      transform: "translateY(75%) rotate(45deg)"
    }, commonChildStyles)
  })), _defineProperty(_ref, '&[x-placement*="top"] .csg-tui-tooltip-arrow', _objectSpread2(_objectSpread2({
    bottom: 0,
    transform: 'translateY(100%)'
  }, commonParentStyles), {}, {
    '&::before': _objectSpread2({
      transform: "translateY(-75%) rotate(45deg)"
    }, commonChildStyles)
  })), _defineProperty(_ref, '&[x-placement*="right"] .csg-tui-tooltip-arrow', _objectSpread2(_objectSpread2({
    left: 0,
    transform: 'translateX(-100%)'
  }, commonParentStyles), {}, {
    '&::before': _objectSpread2({
      transform: "translateX(75%) rotate(45deg)"
    }, commonChildStyles)
  })), _defineProperty(_ref, '&[x-placement*="left"]  .csg-tui-tooltip-arrow', _objectSpread2(_objectSpread2({
    right: 0,
    transform: 'translateX(100%)'
  }, commonParentStyles), {}, {
    '&::before': _objectSpread2({
      transform: "translateX(-75%) rotate(45deg)"
    }, commonChildStyles)
  })), _ref;
};
var Tooltip = /*#__PURE__*/React.forwardRef(function Tooltip(props, ref) {
  var theme = useSafeThemeUI(props);
  var _props$variant = props.variant,
    variant = _props$variant === void 0 ? THEME_UI_DEFAULT_KEY : _props$variant,
    border = props.border,
    _props$arrow = props.arrow,
    arrow = _props$arrow === void 0 ? false : _props$arrow,
    _props$borderedArrow = props.borderedArrow,
    borderedArrow = _props$borderedArrow === void 0 ? false : _props$borderedArrow,
    _props$borderedArrowB = props.borderedArrowBackgroundColor,
    borderedArrowBackgroundColor = _props$borderedArrowB === void 0 ? get(theme, "tooltips.".concat(variant, ".bg"), 'white') : _props$borderedArrowB,
    _props$borderedArrowB2 = props.borderedArrowBorderColor,
    borderedArrowBorderColor = _props$borderedArrowB2 === void 0 ? undefined : _props$borderedArrowB2,
    arrowProps = props.arrowProps,
    children = props.children,
    _props$disableFocusLi = props.disableFocusListener,
    disableFocusListener = _props$disableFocusLi === void 0 ? false : _props$disableFocusLi,
    _props$disableHoverLi = props.disableHoverListener,
    disableHoverListener = _props$disableHoverLi === void 0 ? false : _props$disableHoverLi,
    _props$disableTouchLi = props.disableTouchListener,
    disableTouchListener = _props$disableTouchLi === void 0 ? false : _props$disableTouchLi,
    _props$enableWheelScr = props.enableWheelScrollListener,
    enableWheelScrollListener = _props$enableWheelScr === void 0 ? false : _props$enableWheelScr,
    _props$enterDelay = props.enterDelay,
    enterDelay = _props$enterDelay === void 0 ? 100 : _props$enterDelay,
    _props$enterNextDelay = props.enterNextDelay,
    enterNextDelay = _props$enterNextDelay === void 0 ? 0 : _props$enterNextDelay,
    _props$enterTouchDela = props.enterTouchDelay,
    enterTouchDelay = _props$enterTouchDela === void 0 ? 700 : _props$enterTouchDela,
    idProp = props.id,
    _props$interactive = props.interactive,
    interactive = _props$interactive === void 0 ? false : _props$interactive,
    _props$leaveDelay = props.leaveDelay,
    leaveDelay = _props$leaveDelay === void 0 ? 0 : _props$leaveDelay,
    _props$leaveTouchDela = props.leaveTouchDelay,
    leaveTouchDelay = _props$leaveTouchDela === void 0 ? 1500 : _props$leaveTouchDela,
    onClose = props.onClose,
    onOpen = props.onOpen,
    openProp = props.open,
    _props$placement = props.placement,
    placement = _props$placement === void 0 ? 'top-start' : _props$placement,
    _props$elevation = props.elevation,
    elevation = _props$elevation === void 0 ? 300 : _props$elevation,
    PopperProps = props.PopperProps,
    title = props.title,
    _props$TransitionComp = props.TransitionComponent,
    TransitionComponent = _props$TransitionComp === void 0 ? Grow : _props$TransitionComp,
    TransitionProps = props.TransitionProps,
    _props$zIndex = props.zIndex,
    zIndex = _props$zIndex === void 0 ? 'tooltip' : _props$zIndex,
    rest = _objectWithoutProperties(props, _excluded);
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    childNode = _useState2[0],
    setChildNode = _useState2[1];
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    arrowRef = _useState4[0],
    setArrowRef = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    hystersisOpen = _useState6[0],
    setHystersisOpen = _useState6[1];
  var ignoreNonTouchEvents = useRef(false);
  var closeTimer = useRef();
  var enterTimer = useRef();
  var leaveTimer = useRef();
  var touchTimer = useRef();
  var hystersisTimer = useRef();
  var _useControlled = useControlled({
      controlled: openProp,
      "default": false,
      name: 'Tooltip',
      state: 'open'
    }),
    _useControlled2 = _slicedToArray(_useControlled, 2),
    openState = _useControlled2[0],
    setOpenState = _useControlled2[1];
  var spaceProps = useMemo(function () {
    return getSpace(rest);
  }, [rest]);
  var open = openState;
  if (process.env.NODE_ENV !== 'production') {
    var _useRef = useRef(openProp !== undefined),
      isControlled = _useRef.current;
    useEffect(function () {
      if (childNode && childNode.disabled && !isControlled && title !== '' && childNode.tagName.toLowerCase() === 'button') {
        console.error(['Tui: you are providing a disabled `button` child to the Tooltip component.', 'A disabled element does not fire events.', "Tooltip needs to listen to the child element's events to display the title.", '', 'Add a simple wrapper element, such as a `span`.'].join('\n'));
      }
    }, [title, childNode, isControlled]);
  }
  var id = idProp ? idProp : useUniqueId(TOOLTIP);
  useEffect(function () {
    return function () {
      clearTimeout(hystersisTimer.current);
      clearTimeout(closeTimer.current);
      clearTimeout(enterTimer.current);
      clearTimeout(leaveTimer.current);
      clearTimeout(touchTimer.current);
    };
  }, []);
  var handleOpen = useCallback(function (event) {
    clearTimeout(hystersisTimer.current);
    setHystersisOpen(true);
    // eslint-disable-next-line no-console
    // console.log(`handleOpen setOpenState(true)`)
    // The mouseover event will trigger for every nested element in the tooltip.
    // We can skip re-rendering when the tooltip is already open.
    // We are using the mouseover event instead of the mouseenter event to fix a hide/show issue.
    setOpenState(true);
    if (onOpen) {
      onOpen(event);
    }
  }, [onOpen, setOpenState]);
  var handleEnter = useCallback(function () {
    var forward = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    return function (event) {
      var childrenProps = children.props;
      // eslint-disable-next-line no-console
      // console.log(`handleEnter childNode: ${childNode}`)
      if (event.type === 'mouseover' && childrenProps.onMouseOver && forward) {
        childrenProps.onMouseOver(event);
      }
      if (ignoreNonTouchEvents.current && event.type !== 'touchstart') {
        return;
      }

      // Remove the title ahead of time.
      // We don't want to wait for the next render commit.
      // We would risk displaying two tooltips at the same time (native + this one).
      if (childNode) {
        childNode.removeAttribute('title');
      }
      clearTimeout(enterTimer.current);
      clearTimeout(leaveTimer.current);
      if (enterDelay || hystersisOpen && enterNextDelay) {
        event.persist();
        enterTimer.current = setTimeout(function () {
          handleOpen(event);
        }, hystersisOpen ? enterNextDelay : enterDelay);
      } else {
        handleOpen(event);
      }
    };
  }, [childNode, children.props, enterDelay, enterNextDelay, handleOpen, hystersisOpen]);
  var _useIsFocusVisible = useIsFocusVisible(),
    isFocusVisible = _useIsFocusVisible.isFocusVisible,
    onBlurVisible = _useIsFocusVisible.onBlurVisible,
    focusVisibleRef = _useIsFocusVisible.ref;
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    childIsFocusVisible = _useState8[0],
    setChildIsFocusVisible = _useState8[1];
  var handleBlur = useCallback(function () {
    if (childIsFocusVisible) {
      setChildIsFocusVisible(false);
      onBlurVisible();
    }
  }, [childIsFocusVisible, onBlurVisible]);
  var handleFocus = useCallback(function () {
    var forward = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    return function (event) {
      // Workaround for https://github.com/facebook/react/issues/7769
      // The autoFocus of React might trigger the event before the componentDidMount.
      // We need to account for this eventuality.
      if (!childNode) {
        setChildNode(event.currentTarget);
      }
      if (isFocusVisible(event)) {
        setChildIsFocusVisible(true);
        handleEnter()(event);
      }
      var childrenProps = children.props;
      if (childrenProps.onFocus && forward) {
        childrenProps.onFocus(event);
      }
    };
  }, [childNode, children.props, handleEnter, isFocusVisible]);
  var handleClose = useCallback(function (event) {
    clearTimeout(hystersisTimer.current);
    hystersisTimer.current = setTimeout(function () {
      setHystersisOpen(false);
    }, 800 + leaveDelay);
    // eslint-disable-next-line no-console
    // console.log(`handleClose setOpenState(false)`)
    setOpenState(false);
    if (onClose) {
      onClose(event);
    }
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(function () {
      ignoreNonTouchEvents.current = false;
    }, get(theme, 'transitions.duration.shortest', 150));
  }, [leaveDelay, onClose, setOpenState, theme]);
  var handleLeave = useCallback(function () {
    var forward = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    return function (event) {
      var childrenProps = children.props;
      // eslint-disable-next-line no-console
      // console.log(`handleLeave childNode: ${event.type}`)
      if (event.type === 'blur') {
        if (childrenProps.onBlur && forward) {
          childrenProps.onBlur(event);
        }
        handleBlur();
      }
      if (event.type === 'mouseleave' && childrenProps.onMouseLeave && event.currentTarget === childNode) {
        childrenProps.onMouseLeave(event);
      }
      clearTimeout(enterTimer.current);
      clearTimeout(leaveTimer.current);
      event.persist();
      leaveTimer.current = setTimeout(function () {
        handleClose(event);
      }, leaveDelay);
    };
  }, [childNode, children.props, handleBlur, handleClose, leaveDelay]);
  var handleTouchStart = useCallback(function (event) {
    ignoreNonTouchEvents.current = true;
    var childrenProps = children.props;
    if (childrenProps.onTouchStart) {
      childrenProps.onTouchStart(event);
    }
    clearTimeout(leaveTimer.current);
    clearTimeout(closeTimer.current);
    clearTimeout(touchTimer.current);
    event.persist();
    touchTimer.current = setTimeout(function () {
      handleEnter()(event);
    }, enterTouchDelay);
  }, [children.props, enterTouchDelay, handleEnter]);
  var handleTouchEnd = useCallback(function (event) {
    if (children.props.onTouchEnd) {
      children.props.onTouchEnd(event);
    }
    clearTimeout(touchTimer.current);
    clearTimeout(leaveTimer.current);
    event.persist();
    leaveTimer.current = setTimeout(function () {
      handleClose(event);
    }, leaveTouchDelay);
  }, [children.props, handleClose, leaveTouchDelay]);
  var handleUseRef = useForkRef(setChildNode, ref);
  var handleFocusRef = useForkRef(focusVisibleRef, handleUseRef);
  // can be removed once we drop support for non ref forwarding class components
  var handleOwnRef = useCallback(function (instance) {
    // #StrictMode ready
    // eslint-disable-next-line react/no-find-dom-node
    setRef(handleFocusRef, ReactDOM.findDOMNode(instance));
  }, [handleFocusRef]);
  // @ts-ignore
  var handleRef = useForkRef(children.ref, handleOwnRef);

  // There is no point in displaying an empty tooltip.
  if (title === '') {
    open = false;
  }

  // For accessibility and SEO concerns, we render the title to the DOM node when
  // the tooltip is hidden. However, we have made a tradeoff when
  // `disableHoverListener` is set. This title logic is disabled.
  // It's allowing us to keep the implementation size minimal.
  // We are open to change the tradeoff.
  var shouldShowNativeTitle = !open && !disableHoverListener;
  var childrenProps = _objectSpread2(_objectSpread2({
    'aria-describedby': open ? id : null,
    title: shouldShowNativeTitle && typeof title === 'string' ? title : null
  }, children.props), {}, {
    ref: handleRef
  });
  var interactiveWrapperListeners = {};
  if (!disableTouchListener) {
    childrenProps.onTouchStart = handleTouchStart;
    childrenProps.onTouchEnd = handleTouchEnd;
  }
  if (enableWheelScrollListener) {
    childrenProps.onWheel = handleLeave();
    if (interactive) {
      interactiveWrapperListeners.onWheel = handleLeave(false);
    }
  }
  if (!disableHoverListener) {
    childrenProps.onMouseOver = handleEnter();
    // childrenProps.onMouseLeave = handleLeave()  // original code
    // update to onMouseOut because onMouseLeave was not working when using a none input Html element
    // to support disable button scenario. wait and see if there are other side-effects from this change.
    childrenProps.onMouseOut = handleLeave();
    if (interactive) {
      interactiveWrapperListeners.onMouseOver = handleEnter(false);
      interactiveWrapperListeners.onMouseLeave = handleLeave(false);
    }
  }
  if (!disableFocusListener) {
    childrenProps.onFocus = handleFocus();
    childrenProps.onBlur = handleLeave();
    if (interactive) {
      interactiveWrapperListeners.onFocus = handleFocus(false);
      interactiveWrapperListeners.onBlur = handleLeave(false);
    }
  }
  if (process.env.NODE_ENV !== 'production') {
    if (children.props.title) {
      console.error(['Tui: you have provided a `title` prop to the child of <Tooltip />.', "Remove this title prop `".concat(children.props.title, "` or the Tooltip component.")].join('\n'));
    }
  }
  var mergedPopperProps = useMemo(function () {
    return arrowRef ? deepmerge({
      popperOptions: {
        modifiers: {
          arrow: {
            enabled: Boolean(arrowRef),
            element: arrowRef
          }
        }
      }
    }, PopperProps) : PopperProps;
  }, [arrowRef, PopperProps]);
  var mergedArrowProps = useMemo(function () {
    if (arrow || borderedArrow || arrowProps) {
      var _arrowProps$bg, _arrowProps$boxShadow;
      return borderedArrowGenerator((_arrowProps$bg = arrowProps === null || arrowProps === void 0 ? void 0 : arrowProps.bg) !== null && _arrowProps$bg !== void 0 ? _arrowProps$bg : borderedArrowBackgroundColor, borderedArrowBorderColor, (_arrowProps$boxShadow = arrowProps === null || arrowProps === void 0 ? void 0 : arrowProps.boxShadow) !== null && _arrowProps$boxShadow !== void 0 ? _arrowProps$boxShadow : get(theme, "tooltips.".concat(variant, ".boxShadow"), undefined), arrowProps === null || arrowProps === void 0 ? void 0 : arrowProps.border, arrowProps === null || arrowProps === void 0 ? void 0 : arrowProps.scale);
    } else return null;
  }, [arrowProps, arrow, borderedArrow, borderedArrowBackgroundColor, borderedArrowBorderColor, variant, theme]);
  var basePopperStyle = useMemo(function () {
    return _objectSpread2(_objectSpread2({
      zIndex: zIndex,
      pointerEvents: interactive ? 'auto' : 'none'
    }, mergedArrowProps !== null && mergedArrowProps !== void 0 ? mergedArrowProps : {}), typeof title !== 'string' ? _defineProperty({}, '.csg-tui-pane', spaceProps) : {});
  }, [interactive, mergedArrowProps, spaceProps, title, zIndex]);

  // eslint-disable-next-line no-console
  // console.log(
  //   `rendering tooltip open: ${open} shouldShowNativeTitle: ${shouldShowNativeTitle}`
  // )
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.cloneElement(children, childrenProps), /*#__PURE__*/React.createElement(Popper, _extends({
    className: "csg-tui-tooltip",
    placement: placement,
    anchorEl: childNode,
    open: childNode ? open : false,
    id: childrenProps['aria-describedby'],
    transition: true
  }, interactiveWrapperListeners, mergedPopperProps, {
    __css: basePopperStyle
  }), function (_ref3) {
    var placementInner = _ref3.placement,
      TransitionPropsInner = _ref3.TransitionProps;
    return /*#__PURE__*/React.createElement(TooltipContent, _extends({
      variant: variant,
      Component: TransitionComponent,
      title: title,
      border: border,
      elevation: elevation,
      placement: placementInner,
      TransitionProps: TransitionProps,
      ComponentTransitionProps: TransitionPropsInner,
      arrow: !!mergedArrowProps,
      ignoreNonTouchEvents: ignoreNonTouchEvents,
      setArrowRef: setArrowRef
    }, rest));
  }));
});

export default Tooltip;
export { arrowGenerator };
//# sourceMappingURL=tooltip.esm.js.map
